<script setup>
import { storeToRefs } from "pinia";
import { computed, ref, onMounted, onBeforeMount } from "vue";
import AgentLayout from "./layouts/AgentLayout.vue";
import PageHeader from "@/components/shared/PageHeader.vue";
import {
  DxDataGrid,
  DxColumn,
  // DxFilterRow,
  DxHeaderFilter,
  DxToolbar,
  DxItem,
  DxPaging,
  DxPager,
  DxSorting,
  DxScrolling,
  DxColumnChooser,
  DxLoadPanel,
  DxStateStoring,
  DxSearchPanel,
} from "devextreme-vue/data-grid";
// import DxDropDownButton from "devextreme-vue/drop-down-button";
// import { default as Button } from "devextreme-vue/button";
import { exportDataGrid } from "devextreme/excel_exporter";
import { exportDataGrid as exportDataGridToPdf } from "devextreme/pdf_exporter";
import { Workbook } from "exceljs";
import saveAs from "file-saver";
import { jsPDF } from "jspdf";
import ActionCell from "@/components/dataCollection/ActionCell.vue";
import addDataCollection from "@/components/dataCollection/addDataCollection.vue";
import editDataCollection from "@/components/dataCollection/editDataCollection.vue";
import deleteDataCollection from "@/components/dataCollection/deleteDataCollection.vue";
import uploadFileDataCollection from "@/components/dataCollection/uploadFileDataCollection.vue";
import mappedFiles from "@/components/dataCollection/mappedFiles.vue";
import deleteFolderAndJob from "../components/jobs/deleteFolderAndFile.vue";
import vensafeDataCollection from "@/components/dataCollection/vensafeDataCollection.vue";
import deleteDataCollectionUrl from "@/components/dataCollection/deleteDataCollectionUrl.vue";
import { useUiStore } from "@/stores/ui";
import { useGridPreferencesStore } from "@/stores/gridPreferences";
import { useDataCollectionStore } from "@/stores/dataCollection";
import { useRouter } from "vue-router";
import { useCompanyStore } from "@/stores/company";

const router = useRouter();
const uiStore = useUiStore();
const gridPreferencesStore = useGridPreferencesStore();
const dataCollectionStore = useDataCollectionStore();
const { companyId } = storeToRefs(useCompanyStore());
const adminPreferenceId = computed(() =>
  localStorage.getItem("adminPreferenceId"),
);
let getModalState = computed(() => uiStore.modalState);
const dataGridRefName = ref(null);
let showClearAll = ref(false);
const allowedOperations = ref(["contains"]);
// const buttonData = ref(["Excel", "CSV", "PDF"]);
let dataCollectionsGrid = ref([]);
const isLoading = ref(true);

const dataCollectionModal = () => {
  uiStore.setModalAction('add_data_collection');
  uiStore.setModalState(true);
};

const addDataSource = () => {
  router.push({ name: "createDataSource" });
};

const dataGrid = computed(() => {
  return dataGridRefName.value.instance;
});

const contentReady = (e) => {
  let sortedExp = getSortedColumns();
  let filterExp = e.component.getCombinedFilter();
  if (filterExp || sortedExp) {
    showClearAll.value = true;
  } else {
    showClearAll.value = false;
  }
  setColumnChooserPosition();
};

const getSortedColumns = () => {
  let allColumns = Array.from(Array(dataGrid.value.columnCount()).keys()).map(
    (index) => dataGrid.value.columnOption(index),
  );
  allColumns = allColumns.find((col) => col.sortIndex != null);
  return allColumns;
};

const setColumnChooserPosition = () => {
  var columnChooserView = dataGrid.value.getView("columnChooserView");
  if (!columnChooserView._popupContainer) {
    columnChooserView._initializePopupContainer();
    columnChooserView.render();
  }
  columnChooserView._popupContainer.option("position", {
    of: dataGrid.value.element(),
    my: "left top",
    at: "left top",
    offset: "0 50",
  });
};

const onEditorPreparing = (e) => {
  if (e.parentType == "filterRow") {
    e.editorOptions.placeholder = "Search";
  }
};

const clearFilter = () => {
  dataGrid.value.clearFilter("row");
  dataGrid.value.clearFilter("header");
  dataGrid.value.clearFilter("search");
  dataGrid.value.clearSorting();
};

const customLoad = async () => {
  await gridPreferencesStore
    .onTableHeader({
      url: "/data-source",
      companyId: companyId.value,
    })
    .catch(() => {});
  let value = gridPreferencesStore.tableHeaders;
  return value;
};

const customSave = (gridState) => {
  gridState.pageIndex = 0;
  gridState.pageSize = 10;
  let value = JSON.stringify(gridState);
  value = value.replace(',"selectedRowKeys":[]', "");

  if (!gridPreferencesStore.tableHeaders) {
    gridPreferencesStore
      .createTableHeaders({
        url: "/data-source",
        headers: value,
        filters: "[]",
        companyId: companyId.value,
      })
      .catch(() => {});
  } else if (value != JSON.stringify(gridPreferencesStore.tableHeaders)) {
    gridPreferencesStore
      .updateTableHeaders({
        url: "/data-source",
        headers: value,
        filters: "[]",
        companyId: companyId.value,
        adminPreferenceId: adminPreferenceId.value,
      })
      .catch(() => {});
  }};

const refreshGrid = () => {
  document.querySelector(".modal-backdrop").remove();
  document.querySelector(".dx-device-desktop").classList.remove("modal-open");
  getDataCollectionsGrid();
};

const onItemClick = (type) => {
  if (type == "Excel") {
    const workbook = new Workbook();
    const worksheet = workbook.addWorksheet("Main sheet");
    exportDataGrid({
      component: dataGrid.value,
      worksheet: worksheet,
      customizeCell: function (options) {
        const excelCell = options;
        excelCell.font = { name: "Arial", size: 12 };
        excelCell.alignment = { horizontal: "left" };
      },
    }).then(function () {
      workbook.xlsx.writeBuffer().then(function (buffer) {
        saveAs(
          new Blob([buffer], { type: "application/octet-stream" }),
          "data-collections.xlsx",
        );
      });
    });
    // e.cancel = true;
  } else if (type == "CSV") {
    const workbook = new Workbook();
    const worksheet = workbook.addWorksheet("Main sheet");
    exportDataGrid({
      component: dataGrid.value,
      worksheet: worksheet,
      customizeCell: function (options) {
        const excelCell = options;
        excelCell.font = { name: "Arial", size: 12 };
        excelCell.alignment = { horizontal: "left" };
      },
    }).then(function () {
      workbook.csv.writeBuffer().then(function (buffer) {
        saveAs(
          new Blob([buffer], { type: "application/octet-stream" }),
          "data-collections.csv",
        );
      });
    });
  } else if (type == "PDF") {
    const doc = new jsPDF("landscape");
    exportDataGridToPdf({
      jsPDFDocument: doc,
      component: dataGrid.value,
      customizeCell: function (options) {
        const { gridCell, pdfCell } = options;
        if (gridCell.rowType === "data") {
          // data columns style
          pdfCell.styles = {
            font: "times",
            fontStyle: "lato",
            fontSize: 8,
            cellWidth: "auto",
          };
        } else {
          // header columns style
          pdfCell.styles = {
            font: "times",
            fontStyle: "lato",
            fontSize: 8,
            fillColor: "#2680EB",
            textColor: "#FFFFFF",
            cellWidth: "auto",
          };
        }
      },
    }).then(() => {
      const pageCount = doc.internal.getNumberOfPages();
      for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        const pageSize = doc.internal.pageSize;
        const pageWidth = pageSize.width ? pageSize.width : pageSize.getWidth();
        const pageHeight = pageSize.height
          ? pageSize.height
          : pageSize.getHeight();
        const header = "Users";
        const headerWidth = doc.getTextDimensions(header).w;
        const footer = `Page ${i} of ${pageCount}`;

        // Header
        doc.text(header, (pageWidth - headerWidth) / 2, 10);

        // Footer
        doc.text(
          footer,
          pageWidth / 2 - doc.getTextWidth(footer) / 2,
          pageHeight - 5,
          { baseline: "bottom" },
        );
      }

      doc.save("data-collections.pdf");
    });
  }
};

const getDataCollectionsGrid = () => {
  dataCollectionStore
    .fetchDataCollectionsGrid(companyId.value)
    .then((res) => {
      dataGrid.value.endCustomLoading();
      dataCollectionsGrid.value = res.data;
      dataCollectionsGrid.value.map((item) => {
        item.description = item.description ? item.description : "—";
      });
      isLoading.value = false;
    })
    .catch(() => {
    });
};

onMounted(() => {
  dataGrid.value.beginCustomLoading();
});

onBeforeMount(() => {
  getDataCollectionsGrid();
});
</script>

<template>
  <AgentLayout>
    <!-- <div class="flex items-center">
        <h4 class="grid-header-title">Data Collection</h4>
        <button :title="checkLoggedInUserRole() ? 'You are not authorized to perform this action!': ''" :disabled="checkLoggedInUserRole()" @click="dataCollectionModal('show')" pill class="grid-header-action-btn">
          Add
        </button> 
      </div> -->

    <PageHeader
      title="Data Source"
      subtitle="Group files, sites, and stores into one source to easily power your agent with all your data at once."
      :showButton="true"
      buttonText="Add"
      @action-click="addDataSource()"
    />
    <addDataCollection
      @refresh="refreshGrid"
      :closeModal="dataCollectionModal"
    />
    <editDataCollection v-if="getModalState" @refresh="refreshGrid" />
    <deleteDataCollection v-if="getModalState" @refresh="refreshGrid" />
    <uploadFileDataCollection v-if="getModalState" @refresh="refreshGrid" />
    <mappedFiles v-if="getModalState" />
    <deleteFolderAndJob v-if="getModalState" @refresh="refreshGrid" />
    <vensafeDataCollection v-if="getModalState" @refresh="refreshGrid" />
    <deleteDataCollectionUrl v-if="getModalState" @refresh="refreshGrid" />

    <!-- <div class="insights-container card">
        <div class="card-header">
          <h2>Key Insights</h2>
        </div>
        
        <div v-if="isLoading" class="flex justify-center mb-3">
          <div class="spinner-border text-success" role="status">
              <span class="visually-hidden">Loading...</span>
          </div>
        </div>
        <div v-else>
          <div v-if="dataCollectionsGrid && dataCollectionsGrid.length" class="card-body insights-grid">
            <div v-for="(insight, index) in dataCollectionsGrid" :key="insight.id || index" class="insight-card">
              <div class="insight-icon">
                <i class="dx-icon-file"></i>
              </div>
              <div class="insight-content">
                <h3>{{ insight.name }}</h3>
                <p>{{ insight.description }}</p>
              </div>
              <div class="insight-actions">
                <ActionCell :data="insight"/>
              </div>
            </div>
          </div>

          <div>
            <div v-if="!dataCollectionsGrid || dataCollectionsGrid.length == 0" class="no-data-available">
              <p class="no-data-text">No data collections available.</p>
            </div>
          </div>
        </div>

      </div> -->

    <section class="grid-white-section main-page-container-animation card">
      <DxDataGrid height="100%"
        :data-source="dataCollectionsGrid"
        ref="dataGridRefName"
        :remote-operations="true"
        :showBorders="false"
        :columnAutoWidth="true"
        :allowColumnReordering="true"
        :allowColumnResizing="true"
        :rowAlternationEnabled="false"
        :showColumnLines="false"
        :showRowLines="true"
        :repaint-changes-only="true"
        :two-way-binding-enabled="true"
        :no-data-text="'No collections yet'"
        @editor-preparing="onEditorPreparing($event)"
        @content-ready="contentReady($event)"
      >
        <DxLoadPanel :show-indicator="true" :show-pane="true" :enabled="true" />
        <DxScrolling mode="standard" :use-native="true" />
        <DxPaging :page-size="10" />
        <DxStateStoring
          :enabled="true"
          type="custom"
          :customSave="customSave"
          :customLoad="customLoad"
        />

        <DxPager
          :visible="true"
          :allowed-page-sizes="[10, 25, 50, 100]"
          :display-mode="'full'"
          :show-page-size-selector="true"
          :show-info="true"
          :show-navigation-buttons="true"
        />

        <DxSearchPanel :width="200" :visible="true" placeholder="Search ..." />
        <DxSorting mode="single" />
        <DxHeaderFilter :visible="true" />
        <DxColumnChooser :enabled="true" mode="select" />

        <DxToolbar>
          <DxItem location="before" template="columnButtonTemplate" />
          <DxItem location="before" template="exportButtonTemplate" />
          <DxItem location="before" template="clearAllTemplate" />
          <DxItem location="after" name="searchPanel" />
        </DxToolbar>

        <DxColumn
          data-field="name"
          caption="Name"
          :allow-header-filtering="false"
          :filter-operations="allowedOperations"
          data-type="string"
          :allow-searching="true"
        />

        <DxColumn
          data-field="description"
          caption="Description"
          :allow-header-filtering="false"
          :filter-operations="allowedOperations"
          data-type="string"
          :allow-searching="true"
        />

        <DxColumn
          data-field="actions"
          caption="Actions"
          :allow-filtering="false"
          :allow-exporting="false"
          :allow-editing="false"
          :allow-sorting="false"
          cell-template="action-cell"
          data-type="object"
        />

        <template #action-cell="{ data }">
          <ActionCell :data="data.data" />
        </template>

        <template #exportButtonTemplate>
          <button
            @click="onItemClick('Excel')"
            type="button"
            class="btn action-btn m-5 font-20"
          >
            <i class="bi bi-filetype-xlsx"></i>
          </button>
        </template>

        <template #columnButtonTemplate>
          <button
            @click="dataGrid.showColumnChooser()"
            type="button"
            class="btn action-btn font-20 m-5"
          >
            <i class="bi bi-columns"></i>
          </button>
        </template>

        <template #clearAllTemplate>
          <div v-if="showClearAll" class="clear-cols">
            <div class="custom-line"></div>
            <button
              @click="clearFilter()"
              type="button"
              class="btn action-btn clear-colums-filter m-5"
            >
              Clear Filters
            </button>
          </div>
        </template>
      </DxDataGrid>
    </section>
  </AgentLayout>
</template>

<style lang="scss" scoped>
/* Header styles removed, now in PageHeader component */

.date-filter {
  width: 180px;
}

.stats-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: var(--spacing-4);
  margin-bottom: var(--spacing-6);

  @media (max-width: 1200px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
}

.stat-card {
  padding: var(--spacing-4);
  display: flex;
  gap: var(--spacing-4);
}

.stat-icon {
  width: 48px;
  height: 48px;
  border-radius: var(--border-radius-md);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
  flex-shrink: 0;

  &.icon-up {
    background-color: var(--success-500);
    color: white;
  }

  &.icon-down {
    background-color: var(--error-500);
    color: white;
  }

  &.icon-neutral {
    background-color: var(--info-500);
    color: white;
  }
}

.stat-content {
  h3 {
    font-size: var(--text-sm);
    color: var(--neutral-600);
    margin: 0 0 var(--spacing-1) 0;
    font-weight: 500;
  }
}

.stat-value {
  font-size: var(--text-2xl);
  font-weight: 600;
  color: var(--neutral-900);
  margin-bottom: var(--spacing-1);
}

.stat-change {
  font-size: var(--text-xs);
  display: flex;
  align-items: center;
  gap: 2px;

  &.trend-up {
    color: var(--success-500);
  }

  &.trend-down {
    color: var(--error-500);
  }
}

.charts-row {
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: var(--spacing-4);
  margin-bottom: var(--spacing-6);

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
  }
}

.chart-container {
  .card-body {
    height: 300px;
  }
}

.details-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: var(--spacing-4);
  margin-bottom: var(--spacing-6);

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
  }
}

.queries-list {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-3);
}

.query-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-bottom: var(--spacing-3);
  border-bottom: 1px solid var(--neutral-100);

  &:last-child {
    border-bottom: none;
  }
}

.query-info {
  display: flex;
  align-items: center;
  gap: var(--spacing-3);
}

.query-rank {
  width: 24px;
  height: 24px;
  background-color: var(--primary-50);
  color: var(--primary-700);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  font-size: var(--text-xs);
}

.query-stats {
  display: flex;
  align-items: center;
  gap: var(--spacing-3);
}

.query-count {
  font-weight: 500;
  color: var(--neutral-700);
  min-width: 60px;
  text-align: right;
}

.query-bar {
  width: 120px;
  height: 6px;
  background-color: var(--neutral-100);
  border-radius: 3px;
  overflow: hidden;

  .query-bar-fill {
    height: 100%;
    background-color: var(--primary-500);
    border-radius: 3px;
  }
}

.query-percentage {
  min-width: 40px;
  font-size: var(--text-sm);
  color: var(--neutral-600);
}

.data-table {
  width: 100%;
  border-collapse: collapse;

  th,
  td {
    padding: var(--spacing-3) var(--spacing-2);
    text-align: left;
    border-bottom: 1px solid var(--neutral-200);
  }

  th {
    font-weight: 600;
    color: var(--neutral-700);
  }

  tr:last-child td {
    border-bottom: none;
  }
}

.rating-bar {
  width: 160px;
  height: 16px;
  background-color: var(--neutral-100);
  border-radius: 8px;
  position: relative;
  overflow: hidden;

  .rating-fill {
    height: 100%;
    background-color: var(--primary-500);
    border-radius: 8px;
  }

  .rating-value {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    font-size: var(--text-xs);
    font-weight: 600;
    color: white;
    text-shadow: 0 1px 2px rgba(0, 0, 0, 0.2);
  }
}

.insights-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: var(--spacing-4);

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }

  .insight-card {
    padding: var(--spacing-4);
    border: 1px solid var(--neutral-200);
    border-radius: var(--border-radius-md);
    display: flex;
    gap: var(--spacing-3);
    width: 500px;
    max-width: 500px;
    &:hover {
      border-color: var(--primary-200);
      background-color: var(--primary-50);

      .insight-icon {
        background-color: var(--primary-500);
        color: white;
      }
    }
  }

  .insight-icon {
    width: 40px;
    height: 40px;
    border-radius: var(--border-radius-md);
    background-color: var(--neutral-100);
    color: var(--neutral-600);
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    transition: all 0.2s ease;
  }

  .insight-content {
    width: 388px;
    h3 {
      margin: 0 0 var(--spacing-1) 0;
      font-size: var(--text-base);
    }

    p {
      margin: 0;
      font-size: var(--text-sm);
      color: var(--neutral-600);
    }
  }

  .insight-actions {
    margin-left: auto;
    gap: var(--spacing-2);
  }
}
</style>
